import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpSelect, ErpTable, Modal, toOptions } from "../../components/erp";
import { errorText } from "../../api/client";
import { gradingApi, GradingBreakupRow, ppDropdownsApi } from "./api";

const today = () => new Date().toISOString().slice(0, 10);

const NewBreakupModal: React.FC<{ trackId: string; arrivalId: string; receivalBreakupId: string; onClose: () => void; onSaved: () => void }> =
  ({ trackId, arrivalId, receivalBreakupId, onClose, onSaved }) => {
  const dd = useQuery({ queryKey: ["pp", "dropdowns"], queryFn: ppDropdownsApi.get, staleTime: 5 * 60_000 });
  const varieties = useMemo(() => toOptions(dd.data?.Data?.Variety, (v) => v.VarietyID, (v) => v.ShortName), [dd.data]);
  const [v, setV] = useState({ VarietyID: "", HLCount: "", CrateQty: "" });
  const [err, setErr] = useState<string | null>(null); const [saving, setSaving] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      await gradingApi.post({ Type: "BreakUpData", BreakUpID: "", ArrivalID: arrivalId });
      const fresh = await gradingApi.get({ TrackID: trackId, ArrivalID: arrivalId, ReceivalBreakupID: receivalBreakupId });
      const newRow = fresh?.GradingBreakUpDetails?.GradingData?.slice(-1)[0];
      if (!newRow) { setErr("Could not locate new breakup row"); return; }
      const r = await gradingApi.post({ Type: "BreakUpData", BreakUpID: newRow.GradingBreakUpID, ArrivalID: arrivalId,
        VarietyID: v.VarietyID, HLCount: v.HLCount, NoCrates: 0, CrateQty: v.CrateQty, ActionType: "AddCrate" });
      if (r.Error) setErr(r.Error); else onSaved();
    } catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title="New Grading Breakup" onClose={onClose} testId="grading-new-modal">
      <form onSubmit={submit} data-testid="grading-new-form">
        <div className="mb-3"><label className="form-label">Variety</label>
          <ErpSelect id="grading-new-variety" value={v.VarietyID} onChange={(val) => setV((s) => ({ ...s, VarietyID: val }))} options={varieties} testId="grading-new-variety" /></div>
        <div className="mb-3"><label className="form-label">HL Count</label>
          <input className="form-control" value={v.HLCount} onChange={(e) => setV((s) => ({ ...s, HLCount: e.target.value }))} data-testid="grading-new-hlcount" /></div>
        <div className="mb-3"><label className="form-label">Crate Weight (Kgs)</label>
          <input className="form-control" value={v.CrateQty} onChange={(e) => setV((s) => ({ ...s, CrateQty: e.target.value }))} data-testid="grading-new-crateqty" /></div>
        {err && <Alert kind="danger" testId="grading-new-error">{err}</Alert>}
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid="grading-new-submit">{saving ? "Saving..." : "Save"}</button>
      </form>
    </Modal>
  );
};

/** Port of v3/PreProcessing/grading-trackid.php — count-wise HL output for a Deheaded lot (or lots combined
 * into a "Mix"), recorded in PpcHLGradingDetails (header) + PpcHLGradingBreakup (one row per HL-count grade). */
export default function GradingPage() {
  const [params, setParams] = useSearchParams();
  const trackId = params.get("TrackID") || "";
  const arrivalId = params.get("ArrivalID") || "";
  const receivalBreakupId = params.get("ReceivalBreakupID") || "";
  const qc = useQueryClient();

  const dd = useQuery({ queryKey: ["pp", "dropdowns"], queryFn: ppDropdownsApi.get, staleTime: 5 * 60_000 });
  const detail = useQuery({ queryKey: ["pp", "grading", trackId, arrivalId, receivalBreakupId], enabled: !!trackId,
    queryFn: () => gradingApi.get({ TrackID: trackId, ArrivalID: arrivalId, ReceivalBreakupID: receivalBreakupId }) });

  const lots = detail.data?.GradingDetails?.ReceivingCountDeatils || [];
  const rows = detail.data?.GradingBreakUpDetails?.GradingData || [];
  const grandTotals = detail.data?.GradingBreakUpDetails?.GrandTotals;
  const machines = dd.data?.Data?.GradingMachine || [];
  const [form, setForm] = useState({ MachineID: "", TransactionDate: today() });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null); const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const selectLot = (arrival: string, breakup: string) => setParams((p) => { p.set("ArrivalID", arrival); p.set("ReceivalBreakupID", breakup); return p; });

  const submitHeader = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setMsg(null);
    try {
      const r = await gradingApi.post({ TrackID: trackId, ArrivalID: arrivalId, ReceivalBreakupID: receivalBreakupId, Type: "", ...form });
      if (r.Error) setMsg({ ok: false, text: r.Error }); else { setMsg({ ok: true, text: "Saved." }); qc.invalidateQueries({ queryKey: ["pp", "grading", trackId] }); }
    } catch (ex) { setMsg({ ok: false, text: errorText(ex) }); } finally { setSaving(false); }
  };

  const addCrate = async (row: GradingBreakupRow) => {
    const qty = window.prompt("Additional crate weight (Kgs)?", "27"); if (!qty) return;
    await gradingApi.post({ Type: "BreakUpData", BreakUpID: row.GradingBreakUpID, ArrivalID: row.ArrivalID, VarietyID: row.Variety,
      HLCount: row.HLCount, NoCrates: row.NoofCrates, CrateQty: qty, ActionType: "AddCrate" });
    qc.invalidateQueries({ queryKey: ["pp", "grading", trackId] });
  };
  const removeCrate = async (row: GradingBreakupRow) => {
    await gradingApi.post({ Type: "BreakUpData", BreakUpID: row.GradingBreakUpID, ArrivalID: row.ArrivalID, VarietyID: row.Variety,
      HLCount: row.HLCount, NoCrates: row.NoofCrates, CrateQty: row.CrateQuantity, ActionType: "RemoveCrate" });
    qc.invalidateQueries({ queryKey: ["pp", "grading", trackId] });
  };

  return (
    <>
      <PageHeader title="Grading" location="Preprocessing" />
      <div className="card mb-3"><div className="card-body">
        <div className="mb-3 col-md-6"><label className="form-label">Lot (from Deheading)</label>
          <ErpSelect id="grading-lot" value={String(arrivalId)}
            onChange={(v) => { const l = lots.find((x) => String(x.ArrivalID) === v); selectLot(v, String(l?.ReceivalBreakupID ?? "")); }}
            options={lots.map((l) => ({ value: String(l.ArrivalID), label: `${l.BillNumber || l.ArrivalID}${l.GradingStatus ? " (Graded)" : ""}` }))}
            placeholder="Select Lot" testId="grading-lot" />
        </div>
        {arrivalId && (
          <form onSubmit={submitHeader} data-testid="grading-header-form">
            <div className="row">
              <div className="form-group col-md-3 mb-3"><label className="form-label">Machine</label>
                <select className="form-select" value={form.MachineID} onChange={(e) => setForm((s) => ({ ...s, MachineID: e.target.value }))} data-testid="grading-machine">
                  <option value="">Select</option>{machines.map((m) => <option key={m.MachineID} value={m.MachineID}>{m.MachineName}</option>)}
                </select></div>
              <div className="form-group col-md-3 mb-3"><label className="form-label">Date</label>
                <input className="form-control" type="date" value={form.TransactionDate} onChange={(e) => setForm((s) => ({ ...s, TransactionDate: e.target.value }))} data-testid="grading-date" /></div>
            </div>
            {msg && <Alert kind={msg.ok ? "success" : "danger"} testId="grading-msg">{msg.text}</Alert>}
            <button className="btn btn-primary" type="submit" disabled={saving} data-testid="grading-submit">{saving ? "Saving..." : "Save"}</button>
          </form>
        )}
      </div></div>

      {arrivalId && (
        <div className="card"><div className="card-body">
          <div className="d-flex justify-content-between mb-3"><h6>Grading Breakup</h6>
            <button className="btn btn-sm btn-success" data-testid="grading-add-breakup" onClick={() => setShowNew(true)}>+ Add Breakup</button></div>
          <ErpTable<GradingBreakupRow> testId="grading-breakup-table" loading={detail.isLoading} error={detail.isError ? errorText(detail.error) : null}
            rows={rows} columns={[{ key: "HLCount", title: "HL Count" }, { key: "GradingQty", title: "Weight" }, { key: "NoofCrates", title: "Crates" },
              { key: "Variety", title: "Variety" }, { key: "Grade", title: "Grade" },
              { key: "Action", title: "Action", render: (r) => (
                <div className="d-flex gap-1">
                  <button className="btn btn-sm btn-outline-success" data-testid={`grading-add-crate-${r.GradingBreakUpID}`} onClick={() => addCrate(r)}>+ Crate</button>
                  <button className="btn btn-sm btn-outline-danger" data-testid={`grading-remove-crate-${r.GradingBreakUpID}`} onClick={() => removeCrate(r)}>- Crate</button>
                </div>
              ) }]} />
          {grandTotals && <p className="mt-2" data-testid="grading-yield">Total HON: {grandTotals.TotalHON} | Total HL: {grandTotals.TotalHL} | Yield: {grandTotals.Yield}%</p>}
        </div></div>
      )}
      {showNew && arrivalId && <NewBreakupModal trackId={trackId} arrivalId={arrivalId} receivalBreakupId={receivalBreakupId} onClose={() => setShowNew(false)}
        onSaved={() => { setShowNew(false); qc.invalidateQueries({ queryKey: ["pp", "grading", trackId] }); }} />}
    </>
  );
}
